import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import styled from 'styled-components';
import Slider from 'react-slick';

import NavigationItem from './navigation-item';
import LeftChevron from './assets/leftChevron.svg?react';
import RightChevron from './assets/rightChevron.svg?react';

import { onSelectExercise } from '../../actions/status';
import { CORRECT, WRONG, MISSING, WAITING_FOR_CORRECTION, INCOMPLETE, scrollToTop } from '../../lib/exercise';
import { HOMEWORK_EXECUTION } from '../../constants/routes';

import IconButton from '../icon-button';

import { DEFAULT, GREY, GREEN, YELLOW, RED, LIGHT_BLUE, LIGHT_GREY_UNDERLINED, LIGHT_GREY } from './navigation-item/types';
import { isEmpty } from 'lodash';

const Wrapper = styled.div`
    height: 48px;
    overflow: hidden;
    flex: 1;
`;

export const ArrowContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const Container = styled.div`
    width: 100%;
    display: flex;
`;

export const NavigationBar = ({
    exercises,
    onSelectExercise,
    currentIndex,
    status,
    answers,
    homeworkTerminated,
    homeworkPaused,
    homeworkResumed,
    correctionsStatus,
    assignmentId,
    assignmentEndDate,
    singleAnswer,
    saveStatus,
    sectionsExercisesCounts,
}) => {
    const sliderRef = useRef();
    const history = useNavigate();
    const location = useLocation();
    const [navigationDisabled, setNavigationDisabled] = useState(false);

    let assignmentClosed = false;
    if (assignmentEndDate) {
        const assignmentEndDateTime = new Date(assignmentEndDate);
        assignmentClosed = assignmentEndDateTime.getTime() < Date.now();
    }

    let firstExerciseIndex = 0;
    let lastExerciseIndex = exercises.length - 1;
    if (!!status.sections && !status.finished && status.correctionsStatus.length === 0) {
        lastExerciseIndex = -1;
        for (let currentSectionIndex = 0; currentSectionIndex < sectionsExercisesCounts.length; currentSectionIndex++) {
            lastExerciseIndex += sectionsExercisesCounts[currentSectionIndex];
            if (currentIndex >= firstExerciseIndex && currentIndex <= lastExerciseIndex) {
                break;
            }

            firstExerciseIndex += sectionsExercisesCounts[currentSectionIndex];
        }
    }

    useEffect(() => {
        sliderRef.current.slickGoTo(currentIndex - firstExerciseIndex);
    }, [currentIndex, firstExerciseIndex]);

    useEffect(() => {
        const disabled = homeworkPaused || homeworkResumed || (homeworkTerminated && assignmentId && !assignmentClosed && !singleAnswer);
        setNavigationDisabled(disabled);
    }, [homeworkPaused, homeworkResumed, homeworkTerminated, assignmentId, singleAnswer, assignmentEndDate, assignmentClosed]);

    const handleSelectExercise = (event, index) => {
        event.preventDefault();
        if (navigationDisabled) {
            return;
        }

        if (location.pathname !== HOMEWORK_EXECUTION) {
            setTimeout(history, 0, HOMEWORK_EXECUTION, { replace: true });
        }
        scrollToTop();
        onSelectExercise(index, status, saveStatus);
    };

    const exerciseIsDone = (exerciseId) => {
        return answers.filter((answer) => answer.exerciseId === exerciseId).some((answer) => !isEmpty(answer.answer));
    };

    const getCorrectionStatus = (exerciseId) => {
        const status = correctionsStatus.find((correctionStatus) => correctionStatus.exerciseId === exerciseId);
        return status ? status.state : MISSING;
    };

    const getItemType = (correctionStatus, executed) => {
        if (homeworkTerminated && assignmentId && !assignmentClosed && !singleAnswer) {
            return executed ? LIGHT_GREY_UNDERLINED : LIGHT_GREY;
        }

        if (homeworkTerminated) {
            switch (correctionStatus) {
                case WRONG:
                    return RED;
                case INCOMPLETE:
                    return RED;
                case CORRECT:
                    return GREEN;
                case MISSING:
                    return GREY;
                case WAITING_FOR_CORRECTION:
                    return YELLOW;
                default:
                    return DEFAULT;
            }
        }

        if (homeworkPaused || homeworkResumed) {
            if (executed) {
                return LIGHT_GREY_UNDERLINED;
            }
            return LIGHT_GREY;
        }

        return executed ? LIGHT_BLUE : DEFAULT;
    };

    const settings = {
        dots: false,
        infinite: false,
        speed: 50,
        slidesToShow: 13,
        slidesToScroll: 1,
        arrows: false,
        swipeToSlide: true,
        focusOnSelect: true,
        initialSlide: 0,
        responsive: [
            {
                breakpoint: 1366,
                settings: {
                    slidesToShow: 9,
                },
            },
            {
                breakpoint: 766,
                settings: {
                    slidesToShow: 6,
                },
            },
        ],
    };

    return (
        <Container>
            <ArrowContainer>
                <IconButton
                    disabled={currentIndex === firstExerciseIndex || homeworkPaused || homeworkResumed}
                    onClick={(e) => handleSelectExercise(e, currentIndex - 1)}
                    size="xsmall"
                    type="tertiary"
                    ariaLabel="Torna a esercizio precedente"
                >
                    <LeftChevron aria-hidden="true" />
                </IconButton>
            </ArrowContainer>
            <Wrapper role="tablist">
                <Slider ref={sliderRef} {...settings} key={`slider-${exercises.length}`}>
                    {exercises.slice(firstExerciseIndex, lastExerciseIndex + 1).map((item, index) => (
                        <NavigationItem
                            selected={currentIndex === firstExerciseIndex + index && location.pathname === HOMEWORK_EXECUTION}
                            index={index}
                            type={getItemType(getCorrectionStatus(item.id), exerciseIsDone(item.id))}
                            key={item.id}
                            id={`navigation-item-id-${item.id}`}
                            onSelectExercise={(event, index) => handleSelectExercise(event, firstExerciseIndex + index)}
                            disabled={navigationDisabled}
                        />
                    ))}
                </Slider>
            </Wrapper>
            <ArrowContainer>
                <IconButton
                    disabled={currentIndex === lastExerciseIndex || homeworkPaused || homeworkResumed}
                    onClick={(e) => handleSelectExercise(e, currentIndex + 1)}
                    size="xsmall"
                    type="tertiary"
                    ariaLabel="Vai a esercizio successivo"
                >
                    <RightChevron aria-hidden="true" />
                </IconButton>
            </ArrowContainer>
        </Container>
    );
};

function mapStateToProps(state) {
    return {
        exercises: state.homework.exercises,
        status: state.status,
        answers: state.status.answers,
        currentIndex: state.status.currentExerciseIndex,
        homeworkTerminated: state.status.finished,
        homeworkPaused: !!state.status.paused,
        homeworkResumed: !!state.status.resumed,
        correctionsStatus: state.status.correctionsStatus,
        assignmentId: state.application.assignmentId,
        assignmentEndDate: state.assignment.endDate,
        singleAnswer: !!state.application.singleAnswer,
        saveStatus: !!state.application.saveStatus,
        sectionsExercisesCounts: state.status.sections?.map((section) => section.exercisesCount),
    };
}

NavigationBar.propTypes = {
    exercises: PropTypes.array,
    status: PropTypes.object,
    answers: PropTypes.array,
    currentIndex: PropTypes.number,
    homeworkTerminated: PropTypes.bool,
    homeworkPaused: PropTypes.bool,
    homeworkResumed: PropTypes.bool,
    correctionsStatus: PropTypes.array,
    assignmentId: PropTypes.string,
    assignmentEndDate: PropTypes.string,
    singleAnswer: PropTypes.bool,
    saveStatus: PropTypes.bool,
    sectionsExercisesCounts: PropTypes.arrayOf(PropTypes.number),
    onSelectExercise: PropTypes.func,
};

const actionCreators = {
    onSelectExercise,
};

export default connect(mapStateToProps, actionCreators)(NavigationBar);
